import { useGLTF } from '@react-three/drei';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { cargarManifiesto, manifiestoCompilado } from './cargarManifiesto';
import { resolverAsset, type AssetResuelto, type AvatarDelEscenario } from './manifiesto';

const MANIFIESTO_QUERY_KEY = ['manifiesto-avatares'] as const;

/**
 * Manifiesto de avatares, pedido una vez por carga de la página.
 * `cargarManifiesto` no falla nunca, así que no hay reintentos.
 */
export function useManifiestoAvatares() {
  return useQuery({
    queryKey: MANIFIESTO_QUERY_KEY,
    queryFn: () =>
      cargarManifiesto({
        fetch: (...a) => fetch(...a),
        dev: import.meta.env.DEV,
        compilado: manifiestoCompilado(),
      }),
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export type AvatarResuelto =
  | { estado: 'resolviendo' }
  | { estado: 'resuelto'; asset: AssetResuelto | null };

/**
 * Asset que corresponde al avatar del escenario (cadena de D-3). Con
 * `undefined` (el escenario todavía no llegó) queda en `resolviendo`.
 */
export function useAssetDelEscenario(avatar: AvatarDelEscenario | undefined): AvatarResuelto {
  const { data: manifiesto } = useManifiestoAvatares();
  const id = avatar?.id;
  const generoAparente = avatar?.generoAparente;
  return useMemo(
    () =>
      manifiesto && id !== undefined && generoAparente !== undefined
        ? { estado: 'resuelto', asset: resolverAsset(manifiesto, { id, generoAparente }) }
        : { estado: 'resolviendo' },
    [manifiesto, id, generoAparente],
  );
}

/**
 * Empieza a bajar el `.glb` del escenario en cuanto se conoce (D-6), para
 * que al llegar a la entrevista ya esté en el caché de `useGLTF`. No
 * bloquea nada: si no terminó, la pantalla muestra el marcador con
 * "Cargando paciente…" y el avatar aparece cuando llega.
 */
export function usePrecargarAvatar(avatar: AvatarDelEscenario | undefined): void {
  const r = useAssetDelEscenario(avatar);
  const url = r.estado === 'resuelto' ? r.asset?.url : undefined;
  useEffect(() => {
    if (url) useGLTF.preload(url);
  }, [url]);
}
